import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../library/components/d-popup.js';
import '../../library/elements/d-section.js';
import '../../library/fields/d-view-info.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { numberWord } from 'src/utilities/text';
import { t } from 'src/i18n';
import type { RecurringEventInCollectionDialogInput } from './recurring-event-in-collection-dialog-input';
import type { RecurringEventInCollectionDialogResult } from './recurring-event-in-collection-dialog-result';
import type { DialogActionInput } from '../../library/components/DialogActionInput';

@customElement('recurring-event-in-collection-dialog')
export class RecurringEventInCollectionDialog extends BaseDialog<
  RecurringEventInCollectionDialogInput,
  RecurringEventInCollectionDialogResult
> {
  @property({ type: String })
  collectionName = '';
  @property({ type: Number })
  eventsCount = 0;
  @property({ type: Boolean })
  removing = false;
  @property({ type: Boolean })
  isMeeting = false;
  width = 400;

  protected get calculatedTitle(): string {
    if (this.removing) {
      return this.calculatedDeletingTitle();
    } else {
      return this.calculatedEditingTitle();
    }
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('content.cancel'), action: 'cancel' },
      {
        name: this.isMeeting
          ? t('collections.recurringEventOnlyThisMeeting')
          : t('collections.recurringEventOnlyThisTask'),
        action: 'single',
      },
      { name: t('collections.recurringEventAll'), action: 'all' },
    ];
  }

  private get text() {
    const bothAll =
      this.eventsCount === 2
        ? t('collections.recurringEventQuantifierBoth')
        : t('collections.recurringEventQuantifierAll');
    const meetingsCount = numberWord(this.eventsCount, true);
    const eventsCount = numberWord(this.eventsCount);
    if (this.removing) {
      if (this.isMeeting) {
        return t('collections.recurringEventRemoveMeetingText', {
          count: meetingsCount,
          collectionName: this.collectionName,
          quantifier: bothAll,
        });
      } else {
        return t('collections.recurringEventRemoveTaskText', {
          count: eventsCount,
          collectionName: this.collectionName,
          quantifier: bothAll,
        });
      }
    } else if (this.isMeeting) {
      return t('collections.recurringEventAddMeetingText', { count: meetingsCount, quantifier: bothAll });
    } else {
      return t('collections.recurringEventAddTaskText', { count: eventsCount, quantifier: bothAll });
    }
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info .content=${this.text}></d-view-info>
      </d-section>
    `;
  }

  protected fetchResult(detail: string | undefined): RecurringEventInCollectionDialogResult {
    switch (detail) {
      case 'cancel':
        return { action: 'cancel' };
      case 'all':
        return { action: 'all' };
      case 'single':
        return { action: 'single' };
    }
    throw new Error('Method not implemented.');
  }

  protected initializeDialog(input: RecurringEventInCollectionDialogInput) {
    this.collectionName = input.collectionName;
    this.eventsCount = input.eventsCount;
    this.removing = input.removing;
    this.isMeeting = input.isMeeting;
  }

  private calculatedEditingTitle() {
    if (this.isMeeting) {
      return t('collections.recurringEventAddMeetingTitle');
    } else {
      return t('collections.recurringEventAddTaskTitle');
    }
  }

  private calculatedDeletingTitle() {
    if (this.isMeeting) {
      return t('collections.recurringEventRemoveMeetingTitle');
    } else {
      return t('collections.recurringEventRemoveTaskTitle');
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'recurring-event-in-collection-dialog': RecurringEventInCollectionDialog;
  }
}
